import Link from 'next/link';
import { PATH_DEMO } from '@/constants/spa-routes';

export const FAQFreshdeskList = [
  {
    question: 'What are AI features for customer support?',
    answer: (
      <div>
        <p>AI in customer support is used primarily for operational process optimization.</p>
        <p>
          Two dimensions of AI capabilities for improvements can be considered, depending on the
          agents’ role.
        </p>
        <p>
          The first dimension includes <b>tools created for agents’ performance enhancement.</b> In
          this domain, optimization becomes possible through routine task automation or
          semi-automation.
        </p>
        <p>
          In the case of up to 100% automation, responses in chats, e-mails, social media, and other
          channels of communication are generated automatically. The human role here is outcomes
          analysis and improvement implementation.
        </p>
        <p>
          In the case of semi-automation, AI provides a human with information or suggestions, and a
          human agent responds to the request by hand. Outcome supervision is a human’s task as
          well.
        </p>
        <p>Below is the list of features examples related to agents’ assistance:</p>
        <ul>
          <li>
            <p>automated responses;</p>
          </li>
          <li>
            <p>response suggestions;</p>
          </li>
          <li>
            <p>sentiment analysis.</p>
          </li>
        </ul>
        <p>
          The key capability that AI provides in the realm of response automation is
          personalization. Each response, the ready-to-send or a suggested one, is tailored to a
          particular customer and a particular case. It’s achievable due to AI model training based
          on conversations and the connection between AI models and databases containing real-time
          information.
        </p>
        <p>
          The second dimension includes{' '}
          <b>
            mechanisms and technologies for optimization in which human agents aren’t involved
            directly.
          </b>{' '}
          These operations are performed under the hood.
        </p>
        <p>Examples of such operations include the following:</p>
        <ul>
          <li>
            <p>personalized product and service recommendations;</p>
          </li>
          <li>
            <p>requests routing;</p>
          </li>
          <li>
            <p>personalized content delivery for self-service;</p>
          </li>
          <li>
            <p>dynamic customer profile.</p>
          </li>
        </ul>
        <p>
          <i>On the operational level,</i> the tasks of AI injection in customer support are the
          reduction of time, effort and costs required for the delivery of personalized and accurate
          information to customers.
        </p>
        <p>
          <i>On the strategic level,</i> the ultimate goal of AI implementation in customer support
          is improved customer experience that, in turn, increases customer loyalty.
        </p>
      </div>
    ),
  },
  {
    question: 'Are CoSupport assistants chatbots?',
    answer: (
      <div>
        <p>
          CoSupport Agent and CoSupport Customer are AI assistants. Compared to chatbots, they are
          capable of handling more complex and creative tasks. 
        </p>
        <p>
          The difference between three popular AI-based tools for customer support is as follows:
        </p>
        <ul>
          <li>
            <p>
              <b>Chatbots</b> are relatively simple tools based on solid algorithms. They follow
              certain If-Else rules and provide pre-programmed answers. The level of personalization
              is zero.
            </p>
          </li>
          <li>
            <p>
              <b>AI chatbots</b> are AI-powered tools for conversations with language models. GPT is
              a popular example of such a solution. Today’s AI chatbots like CoSupport Customer have
              additional features that help them connect to other tools or perform certain
              additional tasks, such as browsing or text summarization. 
            </p>
          </li>
          <li>
            <p>
              <b>AI assistants</b> are AI-based solutions that enrich other tools’ functionality
              with new capabilities. For example, CoSupport Agent integration with Freshdesk extends
              the CRM’s ability to manage communication with customers and sales using AI skills.
              The primary task of the integration is to create personalized response suggestions. 
            </p>
          </li>
        </ul>
        <p>
          The key AI assistants’ difference is that they are embedded naturally into existing tools
          to automate processes and enhance users’ performance. 
        </p>
        <p>
          In the case of CoSupport Agent and CoSupport Customer as AI assistants, they are the
          examples of Freshdesk integrations. The solutions integrate naturally to Freshdesk, and
          customer support agents can use their functionality within the same interface while
          leveraging additional features.
        </p>
      </div>
    ),
  },
  {
    question: 'How do CoSupport Agent and CoSupport Customer integrate with Freshdesk?',
    answer: (
      <div>
        <p>
          Both solutions integrate with Freshdesk directly, meaning that agents use the same
          Freshdesk interface enriched with AI-driven features.
        </p>
        <p>
          CoSupport Agent integration into Freshdesk is quick and easy and includes the following
          steps:
        </p>
        <ul>
          <li>
            <p>install the CoSupport Agent browser extension;</p>
          </li>
          <li>
            <p>log in to Freshdesk;</p>
          </li>
          <li>
            <p>navigate to Freshdesk to process requests;</p>
          </li>
          <li>
            <p>AI creates the response suggestions;</p>
          </li>
          <li>
            <p>edit the best suggestion if needed and send a response.</p>
          </li>
        </ul>
        <p>
          Suggestions are based on conversations with a particular customer; hence, they are
          personalized and reflect all “knowledge” of a particular company’s products and services
          that an AI assistant comprehended during the model training.
        </p>
        <p>
          CoSupport Customer integrates with a chatbot on your web site. You provide CoSupport AI
          team with an API key for your chatbot, and a chatbot starts generating responses
          autonomously.
        </p>
      </div>
    ),
  },
  {
    question: 'How data privacy and data security are guaranteed in CoSupport AI solutions?',
    answer: (
      <div>
        <p>
          CoSupport AI uses exceptional measures to guarantee data security. Below is the list of
          the key approaches:
        </p>
        <ul>
          <li>
            <p>
              Data that is used for CoSupport Agent  training is anonymised, meaning that all
              personal information is removed from databases. Thus, only impersonal information is
              used for training.
            </p>
          </li>
          <li>
            <p>
              In both solutions, operations with data are performed on CoSupport AI’s side, with
              data storage on dedicated servers.  Moreover, all data are used for one particular
              project needs; project A’s data is never used for project B.
            </p>
          </li>
        </ul>
      </div>
    ),
  },
  {
    question: 'How do CoSupport™ Agent and CoSupport Customer for Freshdesk work?',
    answer: (
      <div>
        <p>
          CoSupport Agent works as a browser extension. Customer support agents download the
          extension while an administrator tunes the system for flawless integration. CoSupport AI
          solution is one of the easiest Freshdesk integrations to implement.
        </p>
        <p>
          When agents work in Freshdesk, they use the following features (in the Freshdesk
          interface):
        </p>
        <ul>
          <li>
            <p>
              <b>Response suggestions with placeholders.</b> Suggestions are based on conversations
              and information on the company’s products and services on which an AI assistant was
              trained.
            </p>
          </li>
          <li>
            <p>
              <b>Semantic analysis of the text with highlighted intents and key details.</b> It
              makes request understanding easier and reduces response time.
            </p>
          </li>
        </ul>
        <p>Both key features aim to enhance customer experience and increase customers' loyalty.</p>
        <p>
          CoSupport Agent also functions as a browser extension. Once you provide the CoSupport AI
          team with an API key for your chatbot, CoSupport Customer begins generating responses
          autonomously, without the involvement of human agents.
        </p>
      </div>
    ),
  },
  {
    question: 'How can I implement CoSupport solutions into Freshdesk? How much time does it take?',
    answer: (
      <div>
        <p>To access the AI assistant’s functionality, you should follow these steps:</p>
        <ol>
          <li>
            <p>
              <b>Data extraction for training models:</b> Data is collected from the sources
              provided by a company. The primary source is Freshdesk, which includes all customer
              correspondence. Additional sources include the knowledge base, documents describing
              products and services, policies, instructions, and guidelines for customer support
              agents. CoSupport Agent works with all types of textual information. We can also use
              call transcripts.
            </p>
          </li>
          <li>
            <p>
              <b>Data anonymization:</b> All data goes through a separate model that anonymizes it.
              Personal characteristics such as names, credit card info, and location (up to 16 types
              of data) will never be used for training our models.
            </p>
          </li>
          <li>
            <p>
              <b>Model training:</b> All materials are fed to the models.
            </p>
          </li>
          <li>
            <p>
              <b>Solution launch:</b> Access is provided to admins and end users (human agents).
            </p>
          </li>
        </ol>
        <p>
          The time required for Freshdesk AI assistant integration depends heavily on the amount of
          data for model training and handling organizational issues. The process can take up to a
          month.
        </p>
      </div>
    ),
  },
  {
    question: 'What data should a company provide for training CoSupport AI’s  models?',
    answer: (
      <div>
        <p>
          Most of the data is sourced from Freshdesk. We utilize the following types of data derived
          from the CRM:
        </p>
        <ul>
          <li>
            <p>Tickets (we need 1000+ tickets for training)</p>
          </li>
          <li>
            <p>Emails</p>
          </li>
          <li>
            <p>Product reviews</p>
          </li>
          <li>
            <p>Comments</p>
          </li>
          <li>
            <p>Call transcripts</p>
          </li>
        </ul>
        <p>Additional data sources include:</p>
        <ul>
          <li>
            <p>Articles from the knowledge base</p>
          </li>
          <li>
            <p>Content from internal and external websites, including FAQ sections</p>
          </li>
          <li>
            <p>Policies</p>
          </li>
          <li>
            <p>Step-by-step support instructions</p>
          </li>
        </ul>
        <p>
          Any documentation used by your customer service agents to perform their tasks serves as a
          valuable data source for feeding the AI models. We handle all types of textual data in
          various formats, including PDF, Excel, Google Sheets, JSON, and more.
        </p>
      </div>
    ),
  },
  {
    question: 'How much does it cost to integrate CoSupport Agent in Freshdesk?',
    answer: (
      <div>
        <p>
          The pricing policy for CoSupport Agent integration with Freshdesk is based on a scheme
          “fixed fee for one agent for one month.” The number of responses within a standard plan is
          limited. When the number of responses exceeds this limit, an additional charge is applied.
          To resume, the monthly charge for CoSupport Agent depends on the customer support team
          size and agents’ workload (the number of requests and, consequently, responses).{' '}
          <Link href={PATH_DEMO.ROOT} prefetch={false}>
            Get the quote.
          </Link>
        </p>
      </div>
    ),
  },
];
